import React, { useEffect, useRef, useState } from "react";
import { useMap, FeatureGroup } from "react-leaflet";
import L from "leaflet";
import { type SavedMapPlotRequestDetail } from "../../types";
import { createRoot } from "react-dom/client";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import PlaceIcon from "@mui/icons-material/Place";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import EditIcon from "@mui/icons-material/Edit";
import { useApiClient } from "@/hooks/useApiClient";
import { updatePlotRequestDetailsAPI } from "../../api/updatePlotRequestDetailsAPI";
import { type AxiosInstance } from "axios";

interface SavedPlotLayerProps {
  data: SavedMapPlotRequestDetail[];
  defaultVisible: boolean;
}

export const SAVED_PLOT_LAYER_NAME = "保存されたプロット情報を表示する";

export const SavedPlotLayer: React.FC<SavedPlotLayerProps> = ({
  data,
  defaultVisible,
}) => {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup>(L.layerGroup());
  const [visible, setVisible] = useState(defaultVisible);
  const { apiClient } = useApiClient();
  const prevDataRef = useRef<SavedMapPlotRequestDetail[] | null>(null);
  const firstMarkerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!map) return;

    const handleOverlayAdd = (e: L.LayersControlEvent): void => {
      if (e.name === SAVED_PLOT_LAYER_NAME) {
        setVisible(true);
      }
    };

    const handleOverlayRemove = (e: L.LayersControlEvent): void => {
      if (e.name === SAVED_PLOT_LAYER_NAME) {
        setVisible(false);
      }
    };

    map.on("overlayadd", handleOverlayAdd);
    map.on("overlayremove", handleOverlayRemove);

    return () => {
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !data) return;

    if (layerRef.current) {
      layerRef.current.clearLayers();
    }

    if (data.length > 0) {
      const markers = data.map((plot) =>
        createSavedPlotMarker(plot, apiClient, map)
      );
      const validMarkers = markers.filter((marker) => marker !== null);

      if (validMarkers.length > 0) {
        firstMarkerRef.current = validMarkers[0];
      }

      validMarkers.forEach((marker) => {
        if (marker) {
          layerRef.current.addLayer(marker);
        }
      });

      if (validMarkers.length > 0 && data !== prevDataRef.current) {
        if (firstMarkerRef.current) {
          const position = firstMarkerRef.current.getLatLng();
          map.setView(position, 17);
        }

        toast.success(
          `インポートしたピンをプロットしました（${validMarkers.length}件）`
        );
        prevDataRef.current = data;
      }
    }

    if (visible) {
      layerRef.current.addTo(map);
    } else {
      map.removeLayer(layerRef.current);
    }
  }, [map, visible, data, apiClient]);

  return <FeatureGroup />;
};

/**
 * プロット情報のポップアップを作成する
 */
const createSavedPlotPopup = (
  plot: SavedMapPlotRequestDetail,
  apiClient: AxiosInstance,
  map: L.Map
): L.Popup => {
  const popupHTML = document.createElement("div");

  // 緯度経度の表示形式を整える
  const formatCoordinates = (lat: number, lng: number): string => {
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  // 住居表示または緯度経度
  const displayLocation = plot.requestAddress
    ? plot.requestAddress
    : plot.substituteLatitude && plot.substituteLongitude
    ? formatCoordinates(plot.substituteLatitude, plot.substituteLongitude)
    : "未設定";

  // 表示ラベルを決定（住居表示または座標）
  const locationLabel = plot.requestAddress ? "住居表示" : "座標";

  // 地番（所在 + 地番）※バックエンドでどちらか片方しかない場合はエラーとしているので、どちらも存在する場合のみ表示。
  const displayChiban =
    plot.requestLocation && plot.requestChiban
      ? `${plot.requestLocation}${plot.requestChiban}`
      : "";

  // 地図コンテナの横幅
  const mapContainerSizeX = map.getSize().x;

  // ポップアップのデフォルトサイズ(px)
  let popupWidth = 300;
  let popupMaxWidth = 300;

  // 地図コンテナ幅375px以下では（300px幅のポップアップ＋余白）がきれいに収まらないので、ポップアップ幅を縮小する
  if (mapContainerSizeX < 375) {
    popupWidth = Math.floor(mapContainerSizeX * 0.8);
    popupMaxWidth = popupWidth;
  }

  // ポップアップ内のReactコンポーネント
  const PopupContent = (): JSX.Element => {
    const [isEditing, setIsEditing] = useState(false);
    const [remarks, setRemarks] = useState(plot.remarks ?? "");
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async (): Promise<void> => {
      try {
        setIsSaving(true);
        const result = await updatePlotRequestDetailsAPI(apiClient, plot.id, {
          remarks,
        });

        if (result) {
          toast.success("備考を保存しました");
          setIsEditing(false);
        } else {
          throw new Error("備考の保存に失敗しました");
        }
      } catch (error) {
        console.error("備考の保存に失敗しました:", error);
        toast.error("備考の保存に失敗しました");
      } finally {
        setIsSaving(false);
      }
    };

    // 画面サイズに応じてアイコンサイズを調整
    const iconSize = mapContainerSizeX < 375 ? "small" : "inherit";

    return (
      <Box sx={{ width: popupWidth }}>
        <FormControl sx={{ width: "100%" }}>
          <FormLabel>
            <PlaceIcon fontSize={iconSize} />
            {locationLabel}
          </FormLabel>
          <Typography
            ml={2}
            my={1}
            fontSize={"0.8rem"}
            variant={"body1"}
            component={"span"}
            sx={{ wordBreak: "break-word" }}
          >
            {displayLocation}
          </Typography>
          <FormLabel>
            <ImportContactsIcon fontSize={iconSize} />
            地番
          </FormLabel>
          <Typography
            ml={2}
            my={1}
            fontSize={"0.8rem"}
            variant={"body1"}
            component={"span"}
            sx={{ wordBreak: "break-word" }}
          >
            {displayChiban}
          </Typography>
        </FormControl>
        <Box mt={2}>
          <Box display="flex" alignItems="center">
            <FormLabel>
              <NoteAltIcon fontSize={iconSize} />
              備考
            </FormLabel>
            <IconButton
              size="small"
              onClick={() => {
                setIsEditing(true);
              }}
              sx={{ ml: 1 }}
              aria-label="編集"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>

          {isEditing ? (
            <>
              <TextField
                value={remarks}
                onChange={(e) => {
                  setRemarks(e.target.value);
                }}
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                sx={{ mt: 1, mb: 1 }}
                size={mapContainerSizeX < 375 ? "small" : "medium"}
              />
              <Grid container justifyContent="flex-end" spacing={1}>
                <Grid item>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setIsEditing(false);
                    }}
                    disabled={isSaving}
                  >
                    キャンセル
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      void handleSave();
                    }}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <CircularProgress
                          size={16}
                          color="inherit"
                          sx={{ mr: 1 }}
                        />
                        保存中...
                      </>
                    ) : (
                      "保存する"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography
              ml={2}
              my={1}
              fontSize={"0.8rem"}
              variant={"body1"}
              component={"div"}
              sx={{
                whiteSpace: "pre-wrap",
                minHeight: "1.5rem",
                wordBreak: "break-word",
              }}
            >
              {remarks || "（備考なし）"}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  createRoot(popupHTML).render(<PopupContent />);

  return L.popup({
    content: popupHTML,
    maxWidth: popupMaxWidth,
  });
};

/**
 * カスタムマーカーアイコンを作成する
 */
const createCustomMarkerIcon = (): L.DivIcon => {
  return L.divIcon({
    className: "saved-plot-marker",
    html: `
      <div style="position: relative; text-align: center;">
        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
          <path fill="#4CAF50" stroke="#ffffff" stroke-width="1" d="M12.5 0C5.596 0 0 5.596 0 12.5C0 21.875 12.5 41 12.5 41S25 21.875 25 12.5C25 5.596 19.404 0 12.5 0z"/>
          <circle cx="12.5" cy="12.5" r="5" fill="#ffffff" />
        </svg>
      </div>
    `,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });
};

/**
 * 保存されたプロットのマーカーを作成する
 */
const createSavedPlotMarker = (
  plot: SavedMapPlotRequestDetail,
  apiClient: AxiosInstance,
  map: L.Map
): L.Marker | null => {
  if (plot.substituteLatitude === null || plot.substituteLongitude === null) {
    return null;
  }

  const popup = createSavedPlotPopup(plot, apiClient, map);
  const customIcon = createCustomMarkerIcon();

  const marker = L.marker([plot.substituteLatitude, plot.substituteLongitude], {
    icon: customIcon,
  }).bindPopup(popup);

  marker.on("click", () => {
    marker.openPopup();
  });

  return marker;
};
